import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Panel from "../../../components/ui/Panel/Panel.jsx";
import Button from "../../../components/ui/Button/Button.jsx";
import Banner from "../../../components/ui/Banner/Banner.jsx";
import Checkbox from "../../../components/forms/Checkbox/Checkbox.jsx";
import OptionCard from "../../../components/forms/OptionCard/OptionCard.jsx";
import FormField from "../../../components/forms/FormField/FormField.jsx";
import Select from "../../../components/forms/Select/Select.jsx";
import { useApp } from "../../../hooks/useApp.js";
import { getFormTypes } from "../../../services/lookupService.js";
import { getToken } from "../../../services/api.js";
import { saveFormType } from "../../../services/returnService.js";
import { listBusinesses } from "../../../services/businessService.js";
import { listTaxPreparerOptions } from "../../../services/taxPreparerService.js";

const NEW_BUSINESS_VALUE = "__new__";

// Matches the current backend seed data (lookup_form_types). Kept as the
// initial state so the step still renders immediately if the lookup fetch
// is slow or fails.
const DEFAULT_RETURN_TYPES = [
  { value: "original", title: "Form2290", sub: "Report and pay tax on vehicles you first used this period." },
  {
    value: "amended",
    title: "Amended return",
    sub: "Add tax because a vehicle got heavier mid-year, or a suspended vehicle went over its mileage limit.",
  },
  { value: "vincorrection", title: "VIN correction", sub: "Fix a wrong VIN on a Schedule 1 you already filed." },
];

const FormTypeStep = ({ onNext, onBack }) => {
  const { state, dispatch, showToast } = useApp();
  const navigate = useNavigate();
  const [returnTypes, setReturnTypes] = useState(DEFAULT_RETURN_TYPES);
  const [formLookupsLoading, setFormLookupsLoading] = useState(true);
  const [returnTypeError, setReturnTypeError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [businessOptions, setBusinessOptions] = useState([]);
  const [businessOptionsLoading, setBusinessOptionsLoading] = useState(true);
  const [businessError, setBusinessError] = useState(false);
  const [preparerOptions, setPreparerOptions] = useState([]);
  const [preparerOptionsLoading, setPreparerOptionsLoading] = useState(true);

  const rt = state.returnType;
  const usingBackend = !!(getToken() && state.returnId);
  // Once the filer has continued past this step at least once this session
  // (state.maxStep only ever grows past 1 via Wizard.jsx's goStep(n+1) on
  // Continue, or the "Continue filing"/My Filings GO_STEP-step-2 bump before
  // landing here), the business they picked already has a return building on
  // it - trucks, month, everything. Switching to a DIFFERENT business at that
  // point can never carry that data over (a return belongs to one business -
  // see RESET_RETURN's comment in AppContext.jsx), so locking the dropdown
  // here instead of letting the filer discover the data-loss after the fact
  // is the point: to file for someone else, start a New Filing.
  const businessLocked = state.maxStep > 1;
  const setRt = (value) => {
    dispatch({ type: "SET_RETURN_TYPE", returnType: value });
    setReturnTypeError(false);
  };

  // Form2290 / Amended Return / VIN Correction / Final Return are mutually
  // exclusive - picking one greys out the other three, since state.returnType
  // only ever holds one of them at a time. Clicking the active filing-type
  // card again clears it (rt = "") so the other two re-enable; unchecking
  // Final Return does the same via handleFinalReturnChange below. Address
  // Change is independent of all four and stays enabled regardless.
  const typeSelected = rt === "original" || rt === "amended" || rt === "vincorrection";
  const finalSelected = rt === "final";

  // Options for the "Business" dropdown - the caller's own saved businesses.
  // Business/Tax Preparer are no longer their own wizard steps (see
  // stepConfig.js); picking one here is what sets state.businessId, which
  // every later step (Trucks' getReturnForBusiness, etc.) depends on.
  useEffect(() => {
    if (!getToken()) {
      setBusinessOptionsLoading(false);
      return;
    }
    let cancelled = false;
    listBusinesses()
      .then((list) => {
        if (!cancelled) setBusinessOptions(list);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setBusinessOptionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Options for the "Tax Preparer" dropdown - same source BusinessStep uses.
  useEffect(() => {
    if (!getToken()) {
      setPreparerOptionsLoading(false);
      return;
    }
    let cancelled = false;
    listTaxPreparerOptions()
      .then((list) => {
        if (!cancelled) setPreparerOptions(list);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setPreparerOptionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleBusinessChange = (e) => {
    const val = e.target.value;
    if (val === NEW_BUSINESS_VALUE) {
      navigate("/dashboard/businesses/form");
      return;
    }
    const id = val ? Number(val) : null;
    // Switching to a genuinely different business must not keep dragging
    // along whatever return (returnId/trucks/totals/...) was already loaded
    // for the PREVIOUS business - otherwise Continue below saves this
    // filing's data onto the OLD business's TaxReturn row, and everything
    // generated from it later (PDF, XML, receipt) shows that old business
    // even though this screen and Review both display the new one (see
    // AppContext's RESET_RETURN for the full explanation).
    if (id !== state.businessId) {
      dispatch({ type: "RESET_RETURN" });
    }
    dispatch({ type: "SET_BUSINESS_ID", businessId: id });
    const biz = businessOptions.find((b) => b.id === id);
    if (biz) dispatch({ type: "SET_BIZ", patch: biz });
    setBusinessError(false);
  };

  // Per-return override, not the business's saved default (see returnTaxPreparerId's
  // comment in AppContext.jsx) - picking a different preparer here must never silently
  // redefine the business's default for every OTHER return of that business too.
  const handlePreparerChange = (e) => {
    dispatch({ type: "SET_RETURN_TAX_PREPARER", taxPreparerId: e.target.value ? Number(e.target.value) : null });
  };

  const handleFinalReturnChange = (e) => {
    const checked = e.target.checked;
    if (checked) {
      dispatch({ type: "SET_RETURN_TYPE", returnType: "final" });
      setReturnTypeError(false);
      return;
    }

    dispatch({ type: "SET_FINAL_RETURN", value: false });
    if (rt === "final") {
      dispatch({ type: "SET_RETURN_TYPE", returnType: "" });
    }
  };

  const handleContinue = async () => {
    let ok = true;
    if (getToken() && !state.businessId) {
      setBusinessError(true);
      ok = false;
    }
    if (!rt) {
      setReturnTypeError(true);
      ok = false;
    }
    if (!ok) return;

    // Amended/VIN-correction returns aren't complete yet - their own later
    // step (Weight Increase, Mileage Exceeded, VIN Correction) collects the
    // rest of the required data and saves the whole formTypeCd payload then.
    if (usingBackend && (rt === "original" || rt === "final")) {
      setSaving(true);
      try {
        const r = await saveFormType(state.returnId, {
          returnType: rt,
          addressChange: state.addressChange,
          taxPreparerId: state.returnTaxPreparerId,
        });
        dispatch({ type: "SET_RETURN", ...r });
      } catch (err) {
        setSaving(false);
        showToast(err.message || "Couldn't save the filing type. Please try again.");
        return;
      }
      setSaving(false);
    }

    onNext();
  };

  useEffect(() => {
    let cancelled = false;
    getFormTypes()
      .then((types) => {
        if (cancelled) return;
        if (Array.isArray(types) && types.length) {
          setReturnTypes(
            types
              .filter((t) => t.code !== "final")
              .map((t) => ({ value: t.code, title: t.title, sub: t.sub }))
          );
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setFormLookupsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Panel padded>
      <div className="font-disp uppercase tracking-[.14em] text-xs text-[var(--accent)] font-semibold">
        Form
      </div>
      <h2 className="text-2xl m-[4px_0]">What are you filing?</h2>
      <p className="text-[var(--muted)] max-w-[62ch] mb-4">Pick one. This decides which fields you'll see next.</p>

      <div className="grid gap-3.5 sm:grid-cols-2 mb-1">
        <FormField
          label="Business"
          required
          hint={
            businessOptionsLoading
              ? "Loading businesses…"
              : businessLocked
              ? "Locked once you've continued - start a New Filing to file for a different business."
              : undefined
          }
        >
          <Select
            value={state.businessId || ""}
            onChange={handleBusinessChange}
            disabled={businessOptionsLoading || businessLocked}
            invalid={businessError}
          >
            <option value="">— Select a business —</option>
            {businessOptions.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
            <option value={NEW_BUSINESS_VALUE}>+ Add new business</option>
          </Select>
          {businessError && <div className="text-xs text-[var(--danger)] mt-1">Pick a business.</div>}
        </FormField>

        <FormField
          label={<>Tax Preparer <span className="text-[var(--dim)] font-normal">· optional</span></>}
          hint={preparerOptionsLoading ? "Loading options…" : "Reuse a preparer you've already saved, if this return has one."}
        >
          <Select
            value={state.returnTaxPreparerId ?? state.biz.taxPreparerId ?? ""}
            onChange={handlePreparerChange}
            disabled={preparerOptionsLoading}
          >
            <option value="">— None —</option>
            {preparerOptions.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </Select>
        </FormField>
      </div>

      <div className="grid gap-3">
        <div className="font-semibold text-sm mb-1">
          Filing type <span className="text-[var(--danger)]">*</span>
        </div>
        {returnTypes.map((o) => (
          <OptionCard
            key={o.value}
            title={o.title}
            subtitle={o.sub}
            selected={rt === o.value}
            onClick={() => setRt(rt === o.value ? "" : o.value)}
            disabled={formLookupsLoading || finalSelected || (typeSelected && rt !== o.value)}
          />
        ))}
        {returnTypeError && <div className="text-xs text-[var(--danger)]">Pick a filing type.</div>}
      </div>

      <div className="mt-4">
        <Checkbox
          id="rtAddrChange"
          title="My address has changed since my last filing"
          subtitle="We'll flag the change to the IRS (Form 2290 Address Change box). No extra details needed."
          checked={state.addressChange}
          onChange={(e) => dispatch({ type: "SET_ADDRESS_CHANGE", value: e.target.checked })}
        />
        <div className="mt-3">
          <Checkbox
            id="rtFinalReturn"
            title="This is my final return"
            subtitle={
              typeSelected
                ? "Clear the filing type selected above to use this instead."
                : "Tells the IRS not to expect another Form 2290 from you. You can still list vehicles and owe tax if you used any this period — the truck list is optional, not skipped."
            }
            checked={state.finalReturn || rt === "final"}
            onChange={handleFinalReturnChange}
            disabled={typeSelected}
          />
        </div>
      </div>

      {rt === "amended" && (
        <Banner tone="info" className="mt-4">
          Next, we'll ask why you're amending this return and walk you through the details on their own screen.
        </Banner>
      )}

      {rt === "vincorrection" && (
        <Banner tone="info" className="mt-4">
          Next, we'll collect the explanation the IRS requires for a VIN correction. You'll list the corrected VIN(s)
          on the Trucks screen after that — <b>no tax is due</b>. Use the form for the tax period you're correcting.
        </Banner>
      )}

      {rt === "final" && (
        <div className="mt-4 pt-4 border-t border-[var(--line)]">
          <Banner tone="info">
            We'll mark this as your <b>final return</b> so the IRS stops expecting a Form 2290 from you. If you don't
            have any vehicles to report, you can skip the truck list on the next step — but if you do, list them as
            usual and any tax due will still apply.
          </Banner>
        </div>
      )}

      <div className="flex justify-between gap-3 mt-6">
        <Button variant="ghost" onClick={onBack}>
          ← Back
        </Button>
        <Button onClick={handleContinue} disabled={saving}>
          {saving ? "Saving…" : rt === "amended" || rt === "vincorrection" ? "Continue →" : "Continue to trucks →"}
        </Button>
      </div>
    </Panel>
  );
};

export default FormTypeStep;
